// Transaction-type labels for the Drawer, Account Details and Transactions
// Report tables. The Kiosk API (qa2) returns `transactionType` as the
// `TransactionType` integer enum; the 5.70 API (dev1/qa1) returns a display
// string. Both are resolved to the same labels here so the icon/colour lookups
// (`getIconFromTitle`, `getColorFromTitle`) keep working on either contract.

/** Swagger `TransactionType` enum → display label. Values not listed are unknown. */
export const TransactionTypeNames: { [key: number]: string } = {
  0: "Purchase",
  1: "Redeem",
  2: "Promo",
  3: "Cancel Purchase",
  4: "Cancel Promo",
  5: "Free Play",
  7: "Comp",
  8: "Add",
  9: "Remove",
  // Daily entitlement grant (written by `GrantDailyEntitlement`). The UI calls
  // the daily entitlement "Free Play", the same wording as type 5.
  11: "Free Play",
  15: "Cancel Redeem",
}

/** 5.70 display string → label. */
export const TransactionTypeAlt = (type: string): string | undefined => {
  const mapping: { [type: string]: string } = {
    "Purchase POS": "Purchase",
    Redeem: "Redeem",
    Bounceback: "Promo",
    Cancel: "Cancel Purchase",
    "Cancel Bounceback": "Cancel Promo",
    "Free Play": "Free Play",
    Promo: "Comp",
    Fill: "Add",
    Bleed: "Remove",
    "Cancel Redeem": "Cancel Redeem",
  }

  return mapping[type]
}

/** `transactionType` exactly as the API sends it (enum number, or a 5.70 string). */
export type ApiTransactionType = string | number | null | undefined

/** Display label for a transaction type, whichever contract produced it. */
export const resolveTransactionTypeLabel = (type: ApiTransactionType): string => {
  if (typeof type === "number") {
    return TransactionTypeNames[type] ?? UnknownTransactionType
  }
  if (typeof type === "string") {
    if (/^\d+$/.test(type)) {
      return TransactionTypeNames[Number(type)] ?? UnknownTransactionType
    }
    return TransactionTypeAlt(type) ?? UnknownTransactionType
  }
  return UnknownTransactionType
}

// Promo, Free Play and Cancel Promo values are entries (points), not money.
const ENTRIES_LABELS = new Set(["Promo", "Free Play", "Cancel Promo"])

/** True when the transaction value is expressed in entries rather than dollars. */
export const isEntriesTransaction = (type: ApiTransactionType): boolean =>
  ENTRIES_LABELS.has(resolveTransactionTypeLabel(type))

export const TransactionTypeNumbers: { [key: string]: number } = {
  Purchase: 0,
  Redeem: 1,
  Promo: 2,
  "Cancel Purchase": 3,
  "Cancel Promo": 4,
  "Free Play": 5,
  Comp: 7,
  Add: 8,
  Remove: 9,
  "Cancel Redeem": 15,
}

/** Enum numbers a Drawer / Transactions Report tile filters on. */
export const transactionTypeFilterNumbers = (title: string): number[] => {
  switch (title) {
    case "Adjustment":
      return [TransactionTypeNumbers["Cancel Purchase"], TransactionTypeNumbers["Cancel Redeem"]]
    case "Free Play":
      return [TransactionTypeNumbers["Free Play"], DailyEntitlementTransactionType]
    default:
      return title in TransactionTypeNumbers ? [TransactionTypeNumbers[title]] : []
  }
}

/** Second enum value shown as "Free Play": the daily entitlement grant. */
export const DailyEntitlementTransactionType = 11

// Default transaction type for unhandled cases.
export const UnknownTransactionType = "Unknown"
